export const meta = {
  id: '42-infinitives-commands-wishes',
  order: 42,
  checkFocus: { mood: ['imperative', 'subjunctive'] },   // what the end-of-lesson check tests first
  titleFi: 'Infinitiivit, käskyt ja toivotukset',
  titleEn: 'Infinitives, commands and wishes',
  lemmaIds: [
    'gūrēñagon',
    'nājumbagon',
    'ostāragon',
    'vindigon',
    'kydȳbagon',
    'mīrēbagon',
    'naejuragon',
    'jorilagon',
    'ospryjagon',
    'toliot',
    'keskydoso',
    'idañe',
    'mentyr',
    'kaerīnnon',
    'idīnnon',
    'gaoso',
    'tolkydoso',
    'hēzīr',
    'bōsirī',
    'sȳrī',
    'rēbagon',
    'itymagon',
    'pradagon',
    'letagon',
    'jodakogon',
    'añuragon',
    'sinkagon',
    'hōzalbar',
    'ōdres',
    'qūvy',
    'vaodekuragon',
    'iōremagon',
    'ours:dīnilaksy',
    'ours:qēlo-tubis',
    'ours:qēlo-ōñītsos',
    'ours:ōño-bantis',
    'ours:drōmo-tubis',
    'ours:kiōho-tubis',
    'ours:arlio-jēdaro-ōñītsos',
    'ours:muño-tubis',
    'ours:kepo-tubis',
    'ours:kirimvo-tubis',
    'ours:siño-tubis',
    'ours:santa',
  ],
  exampleIds: [
    'ex-6434bfaf2166',
    'ex-02f1d172ee16',
    'ex-ce830f4ad0c3',
    'hotd-s1e08-008',
    'hotd-s1e06-015',
    'got-s5e04-012',
    'hotd-s1e04-004',
    'got-s6e01-006',
    'got-s7e02-006',
    'got-s6e02-001',
    'got-s3e05-008',
    'hotd-s2e04-003',
    'hotd-s2e04-005',
    'hotd-s1e04-008',
    'got-s5e01-005',
    'ex-33e29858e837',
    'hotd-s1e07-011',
    'got-s3e05-003',
    'hotd-s3e08-003',
    'got-s7e02-004',
    'got-s3e10-005',
    'hotd-s1e06-002',
    'got-s4e10-003',
    'ex-aaf787c4ca15',
    'hotd-s3e07-006',
    'hotd-s3e04-002',
    'hotd-s1e07-012',
    'ex-bb8cf0f1a2de',
    'hotd-s3e03-006',
    'ex-322d35e66806',
    'ex-5fab68e6d60d',
    'got-s7e04-001',
    'ex-35d9d49abd3c',
    'ex-404a45b964aa',
    'hotd-s1e08-009',
    'hotd-s1e06-010',
    'hotd-s3e08-004',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Conjugation',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=Urnegon',
    'https://wiki.languageinvention.com/index.php?title=Nājumbagon',
    'https://wiki.languageinvention.com/index.php?title=Gaomagon',
    'https://archiveofourown.org/works/41199249/chapters/105725109',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['imperative', 'infinitives'],
  drills: { mood: ['conditional', 'optative'] },
  showLines: ['hotd-s1e08-008', 'hotd-s1e06-015', 'got-s5e04-012', 'hotd-s1e04-004', 'got-s6e01-006', 'got-s7e02-006', 'got-s6e02-001', 'got-s3e05-008', 'hotd-s2e04-003', 'hotd-s2e04-005', 'hotd-s1e04-008', 'got-s5e01-005', 'hotd-s1e07-011', 'got-s3e05-003', 'hotd-s3e08-003', 'got-s7e02-004', 'got-s3e10-005', 'hotd-s1e06-002', 'got-s4e10-003', 'hotd-s3e07-006', 'hotd-s3e04-002', 'hotd-s1e07-012', 'hotd-s3e03-006', 'got-s7e04-001', 'hotd-s1e08-009', 'hotd-s1e06-010', 'hotd-s3e08-004'],
  pronunciation: { focus: 'command-stress', lines: ['got-s5e04-012', 'got-s6e01-006', 'got-s7e02-006'] },
  letter: {
    fi: 'Toivota kumppanillesi hyvää kolmella eri rakenteella.',
    en: 'Wish your partner well with three different constructions.',
  },
  scene: 'got-s4e10-1',
}

# Infinitiivit, käskyt ja toivotukset

Infinitiivit ja käskyt kuuluvat yhteen, koska moni käsky, kehotus ja toivotus rakennetaan infinitiivillä. Ensin infinitiivien muodot ja käytöt, sitten käskyt, kehotukset ja toivotukset.

## Infinitiivit: olla vai ei olla

Duolingon infinitiiviosio opetti perusrakenteet: <Hv glossed>urnegon kostas</Hv> ’voi nähdä’, <Hv glossed>ȳdragon
vale sytilības</Hv> ’miehen pitäisi puhua’, kausatiivi <Hv fi="pakottaa">sahagon</Hv> ja infinitiivi käskyissä.
Wikin tapaluokkasivu (*Verb Moods*) toteaa, että joissakin kielioppiperinteissä infinitiiviä
pidetään omana tapaluokkanaan. Korkeavalyriassa infinitiivillä on niin monta erityistehtävää, että
ajatus osuu kohdalleen. Tällä oppitunnilla katsotaan, **mitä eri infinitiivejä** on ja **mihin
kaikkeen** niitä käytetään. Käskyihin palataan oppitunnin jälkimmäisessä osassa.

## Kuusi infinitiiviä

Duolingossa näit preesensin aktiivin infinitiivin (sanakirjamuodon) ja passiivin osiossa preesensin
ja perfektin passiivin infinitiivit. Infinitiivejä on sekä aktiivissa että passiivissa, preesensissä,
aoristissa ja perfektissä:

<Paradigm lemma="ipradagon" filter="infinitive" />

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th></th><th>aktiivi</th><th>passiivi</th></tr></thead>
  <tbody>
    <tr><td>preesens</td><td><Hv glossed>ipradagon</Hv> ’syödä’</td><td><Hv glossed>ipradakson</Hv> ’tulla syödyksi’</td></tr>
    <tr><td>aoristi</td><td><Hv glossed>ipradigon</Hv> ’syödä (yleensä)’</td><td><Hv glossed>ipradiakson</Hv> ’tulla syödyksi (yleensä)’</td></tr>
    <tr><td>perfekti</td><td><Hv glossed>iprattagon</Hv> ’olla syönyt’</td><td><Hv glossed>iprattakson</Hv> ’olla syöty’</td></tr>
  </tbody>
</table>
</div>

Wikin mukaan **futuurin infinitiiviä ei ole** (Peterson kertoi tämän vastatessaan kysymykseen
Tumblrissa).

### Aoristin infinitiivi: yleisesti, tapana

Kuten näit <LessonLink n={38}>oppitunnilla 38</LessonLink> (<Hv glossed>ȳdrassigon</Hv> ’osata puhua’), aoristin infinitiivi kertoo yleisestä
asiasta:

<Example id="ex-6434bfaf2166" />

<Interlinear hv="Dohaer|iakson raq|an." gloss="palvella-AOR.PASS.INF pitää-PRS.1SG" fi="Pidän siitä, että minua palvellaan." />

### Perfektin infinitiivi: ”olla tehnyt”

Suomessakin on perfektin infinitiivi, tosin apuverbillä muodostettu: *olla syönyt*, *olla mitattu*.

<Example id="ex-02f1d172ee16" />

<Interlinear hv="Kydȳpt|akson sȳz kes|os." gloss="mitata-PRF.PASS.INF hyvä-NOM olla-FUT.SBJV.3SG" fi="Olisi hyvä olla mitattu." />

<Compare fi="olla mita|ttu" hv="kydȳpt|akson" label="perfektin passiivin infinitiivi" />

Huomaa myös <Hv l="sagon">kesos</Hv>. Se on <Hv glossed>sagon</Hv>-verbin futuurin subjunktiivi ’olisi’, sama
”haluaisin, olisi” -subjunktiivi, joka tuli vastaan <LessonLink n={39}>oppitunnilla 39</LessonLink>.

### Passiivin infinitiivi: kohde pysyy subjektina

<Example id="ex-ce830f4ad0c3" />

<Interlinear hv="Havon sīr iprad|akson kost|as." gloss="leipä-NOM nyt syödä-PASS.INF voida-PRS.3SG" fi="Leivän voi nyt syödä. (Leipä voi nyt tulla syödyksi.)" />

Tässä suomi ja valyria eroavat selvästi. Suomeksi sanotaan ”leivän voi syödä”: infinitiivi on
aktiivissa ja verbi persoonaton. Valyriaksi sanotaan kirjaimellisesti ”leipä voi tulla syödyksi”:
leipä on subjekti, ja infinitiivi on passiivissa.

Sama ilmiö näkyy käskyissä. Mestari Gerardys kertoo Aegon Valloittajasta:

<Line id="hotd-s1e08-008" />

<Interlinear hv="Guēsi ropak|akson Āegon ūnd|as." gloss="puu-NOM.PL kaataa-PASS.INF Aegon-NOM käskeä-PRF.3SG" fi="Aegon käski kaataa puut." />

Sanakirjan mukaan <Hv glossed>urnegon</Hv> ’nähdä’ tarkoittaa infinitiivin kanssa myös ’käskeä tekemään’.
Suomeksi ”käski kaataa” on aktiivissa, valyriaksi ”käski puiden tulla kaadetuiksi” passiivissa.

Lääkärin karu ehdotus koostuu pelkistä passiivin infinitiiveistä:

<Line id="hotd-s1e06-015" />

## Infinitiivi nominina

Infinitiivi voi olla lauseen subjekti tai objekti aivan kuin substantiivi. Wikin kielioppisivulla
on tähän tärkeä sääntö: **kun infinitiivi on olla-verbin subjekti, predikatiiviadjektiivi on
maasuvun yksikössä.** Daenerys:

<Line id="got-s5e04-012" />

<Interlinear hv="Gūrogon Jazdano glaeson aōhon ist|os daor." gloss="ottaa-INF Harpyija-GEN elämä-ACC sinun.oma-TERR.NOM olla-PRF.SBJV.3SG ei" fi="Harpyijan hengen ottaminen ei ollut sinun asiasi." />

<Hv glossed>Aōhon</Hv> ’sinun’ on maasuvussa, koska subjekti on infinitiivilauseke *gūrogon … glaeson*.
Wikin tunnettu esimerkki samasta rakenteesta on Hamletin <Hv glossed>Sagon iā sagon daor</Hv> ’olla vai ei
olla’. Huomaa, että kielteisen infinitiivin perään tulee pelkkä <Hv>daor</Hv>, sillä infinitiivillä
ei ole subjunktiivia.

Infinitiivi voi olla myös postposition täydennys:

<Line id="hotd-s1e04-004" />

<Interlinear hv="Dīn|akson toliot, gaoso gaom|agon kost|as." gloss="vihkiä-PASS.INF jälkeen oma.tapa tehdä-INF voida-PRS.3SG" fi="Vihkimisen jälkeen voi tehdä niin kuin haluaa." />

<Note>
**Suomessa tähän käytetään teonnimeä:** ”vihkimisen jälkeen”, ”syömisen jälkeen”. Suomi tekee
verbistä substantiivin *-minen*-johtimella, valyria taas käyttää infinitiiviä sellaisenaan, ilman
johdosta. Kirjoittaessa: kun suomeksi tekisi mieli käyttää *-minen*-muotoa, valyriaksi riittää
usein infinitiivi.
</Note>

## Tarkoituksen infinitiivi: ”tekemään”

Wikin mukaan infinitiivi voi ilmaista tarkoitusta, kuten englannin *(in order) to*. Zanrush
Meereenin aukiolla:

<Line id="got-s6e01-006" />

Ensimmäisen virkkeen sivulauseessa <Hv glossed>jemī ivīlībagon</Hv> ’taistelemaan puolestanne’ on tarkoitusta ilmaiseva
infinitiivi.

Melisandre:

<Line id="got-s7e02-006" />

<Hv glossed>Avy baelagon … yne jittas</Hv> ’lähetti minut auttamaan sinua’.

<Compare fi="autta|maan" hv="bael|agon" label="tarkoitus: 3. infinitiivin illatiivi ~ infinitiivi" />

Tässä suomi on lähellä valyriaa: suomen **3. infinitiivin illatiivi** (*auttamaan, taistelemaan*)
hoitaa saman tehtävän. Ero on vain siinä, että suomessa infinitiivillä on eri tehtäviin eri muodot
(*auttaa, auttamaan, auttamassa, auttaminen*), kun taas valyria selviää yhdellä.

## Epäsuora käsky ja pyyntö

Kun jotakuta käsketään tai pyydetään tekemään jotakin, tehtävä ilmaistaan infinitiivillä. Punainen
pappi rukoilee:

<Line id="got-s6e02-001" />

<Interlinear hv="Zȳhys ōñoso jehik|agon Āeksiot ep|i." gloss="hänen valo-INS loistaa-INF Herra-DAT pyytää-PRS.1PL" fi="Pyydämme Herraa loistamaan valollaan." />

Daenerys Tahrattomille:

<Line id="got-s3e05-008" />

<Hv glossed>Keskydoso gaomagon</Hv> ’tekemään samoin’. Suomessa vastaava muoto on 3. infinitiivin
illatiivi: *pyydämme loistamaan*, *käske tekemään*.

## Verbejä, joita seuraa infinitiivi

Duolingon ohjeet esittelivät sanajärjestyksen **(muut lauseenjäsenet) (infinitiivi) (pääverbi)**.
Sarjoissa on paljon verbejä, joiden täydennyksenä on infinitiivi. Tässä muutama hyödyllinen:

- <Hv glossed>sylugon</Hv> ’yrittää’ (Duolingosta tuttu):

<Line id="hotd-s2e04-003" />

- <Hv glossed>ostāragon</Hv> ’päättää, olla aikeissa’:

<Line id="hotd-s2e04-005" />

- <Hv glossed>nājumbagon</Hv> ’epäillä’ – huomaa, että infinitiivin subjekti <Hv glossed>rūs</Hv> ’lapsi’ on
  nominatiivissa:

<Line id="hotd-s1e04-008" />

- <Hv glossed>gūrēñagon</Hv> ’oppia’ ja <Hv glossed>bēvilagon</Hv> ’täytyä’ samassa lauseessa:

<Line id="got-s5e01-005" />

- <Hv glossed>vindigon</Hv> ’kieltäytyä’:

<Example id="ex-33e29858e837" />

- <Hv>kostagon</Hv> kieltomuodossa:

<Line id="hotd-s1e07-011" />

<Note>
Peterson on itse sanonut repliikin *mērpī*-sanan virheeksi: se on adjektiivin muoto, ja adverbina oikea muoto olisi <Hv l="mērprī">mērprī</Hv> tai <Hv l="mērprī">mērpirī</Hv> (Petersonin Conlang Dialogue -selitykset, 2022).
</Note>

<Note>
Yllä <Hv glossed>bēvulza</Hv> on verbin <Hv>bēvilagon</Hv> futuuri. Se on johdettu verbistä <Hv>ilagon</Hv>
ja perii siltä futuurin epäsäännöllisen *ul*-vartalon. Tästä lisää <LessonLink n={43}>oppitunnilla 43</LessonLink>.
</Note>

## Harjoituslauseita

<Practice hv="Aōt bardugon jaelan." fi="Haluan kirjoittaa sinulle." />

<Practice hv="Valyrio udriri ȳdragon gūrēni." fi="Opettelemme puhumaan valyrian kieltä." />

<Practice hv="Rūniapos sīr pikībakson kostas." fi="Kirjeen voi nyt lukea. (Kirje voi nyt tulla luetuksi.)" />

<Practice hv="Ziry jorilagon jaelza." fi="Hän haluaa levätä." />

## Muistilista

- Kuusi infinitiiviä: preesens, aoristi ja perfekti, kukin aktiivissa ja passiivissa. Futuuria ei ole.
- Passiivin infinitiivi pitää kohteen subjektina: ”leipä voi tulla syödyksi”.
- Infinitiivi subjektina → predikatiivi maasuvun yksikössä.
- Tarkoitus ja epäsuora käsky: pelkkä infinitiivi (suomessa *-maan*).
- Infinitiivin kielto: <Hv>daor</Hv> perään, ei subjunktiivia.

## Käskyt, kehotukset ja toivotukset

Duolingon käskyosio (*Commands*) opetti perusasiat: imperatiivi *-ās* / *-ātās* (vokaalivartaloilla
pitkä vartalovokaali: <Hv>kelīs</Hv>, <Hv>kelītīs</Hv>), kielteinen käsky infinitiivillä ja
<Hv>daor</Hv>-sanalla (<Hv fi="älä syö">ipradagon daor</Hv>) sekä kolmannelle persoonalle annettu käsky:
vokatiivi + infinitiivi (<Hv glossed>taobus keligon</Hv> ’pankaa poika lopettamaan’). Tässä osassa
käydään läpi kaikki, mitä tarvitset kirjeen lopussa: *tehdään niin*, *anna minun*, *voi hyvin*,
*eläköön*.

## Imperatiivi kolmessa aikamuodossa

Wikin kielioppisivun mukaan imperatiivilla on kolme aikamuotoa: **preesens, aoristi ja futuuri**.
Kaikista on muodot vain 2. persoonassa, yksikössä ja monikossa.

<Paradigm lemma="ipradagon" filter="imperative.active" />

### Futuurin imperatiivi: ”sinun tulee”

Futuurin imperatiivi on juhlallinen, ja siinä on ohjeen tai määräyksen sävy: englannin *you shall*.
Daenerys vapautetuille Tahrattomille:

<Line id="got-s3e05-003" />

<Interlinear hv="Jenti jevi jemēle iderēb|ilātās, qogrondo jevo hēdrȳ." gloss="johtaja-ACC teidän-SOL.ACC te.itse-ACC valita-FUT.IMP.2PL rivi-GEN.COL teidän-TERR.GEN.COL keskeltä" fi="Valitsette itse johtajanne omista riveistänne." />

Rhaenyra antaa sotilaalliset käskynsä sekä preesensin että futuurin imperatiivilla:

<Line id="hotd-s3e08-003" />

Välitön käsky on preesensissä (<Hv glossed>pradās</Hv> ’aseta’, <Hv glossed>jās</Hv> ’mene’, <Hv glossed>sēnās</Hv> ’tapa’),
pitkäkestoinen tehtävä futuurissa (<Hv glossed>vumbilās</Hv> ’jatkat hyökkäystä’).

<Note>
**Suomi ei auta.** Suomen imperatiivilla ei ole aikamuotoja. Sävyn tavoittaa esimerkiksi ”sinun
tulee valita”, ”valitsette” tai juhlava ”valitkaa vastedes”. Aoristin imperatiivista
(<Hv>ipradiās</Hv>) wikissä ei ole yhtään käyttöesimerkkiä. Aoristin yleisen merkityksen perusteella
se tarkoittaisi ”tee aina näin”, mutta tämä on oma päätelmämme, ei wikin.
</Note>

<Note>
**Paino.** Wikin taivutussivun mukaan kaikkien imperatiivimuotojen paino on **viimeisellä
tavulla**: <Hv>ipradās</Hv> painottuu tavulle *-dās*, <Hv>kelītīs</Hv> tavulle *-tīs*.
</Note>

### Passiivin imperatiivi

Duolingon ohjeiden mukaan myönteinen käsky annetaan passiivissa passiivin 2. persoonan muodolla.
Yksikössä ja monikossa muoto on sama: <Hv glossed>ipradāks</Hv> ’tule syödyksi, tulkaa syödyiksi’. Daenerys toivottaa
Punaisen papittaren tervetulleeksi:

<Line id="got-s7e02-004" />

<Hv glossed>Kesīr drējī jiōrilāks</Hv> ’olet täällä todella tervetullut’ – kirjaimellisesti ’sinut otetaan
täällä vastaan’. <Hv glossed>Jiōrilāks</Hv> on verbin <Hv>jiōragon</Hv> futuurin passiivin 2. persoona.
Muodon puolesta se voi olla indikatiivi tai imperatiivi; kirjeen alkuun se sopii joka tapauksessa.

## ”Anna minun”: datiivi + infinitiivi

Wikin mukaan luvan pyytämiseen ja antamiseen käytetään infinitiiviä, jonka edessä on **datiivi**
(se, jonka annetaan tehdä jotakin). Daenerys Yunkain porteilla:

<Line id="got-s3e10-005" />

<Interlinear hv="Ynot rēb|agon." gloss="minä-DAT kulkea.läpi-INF" fi="Anna minun mennä ohi." />

<Line id="hotd-s1e06-002" />

<Line id="got-s4e10-003" />

Wikin mukaan rakenteen perään voisi lisätä <Hv>gaomagon</Hv>-verbin imperatiivin (<Hv glossed>gaomātās</Hv>
’sallikaa’), mutta tavallisesti se jätetään pois. Sanakirjassa <Hv glossed>gaomagon</Hv> on juuri tässä
merkityksessä: ’sallia jonkun (datiivi) tehdä jotakin (infinitiivi)’.

<Compare fi="anna minu|n mennä" hv="yno|t rēbagon" label="lupa: genetiivi + infinitiivi ~ datiivi + infinitiivi" />

## ”Tehdään!”: *īlot* + infinitiivi

Sama rakenne toimii monikon 1. persoonassa: *anna meidän* = *tehdään*. Peterson Twitterissä:

<Example id="ex-aaf787c4ca15" />

<Interlinear hv="Sesīr īlot j|agon!" gloss="nyt me-DAT mennä-INF" fi="Mennään nyt!" />

Ja lohikäärmeratsastajat:

<Line id="hotd-s3e07-006" />

<Line id="hotd-s3e04-002" />

Rhaenyra kosii Daemonia samalla rakenteella:

<Line id="hotd-s1e07-012" />

<Note>
**Kaksi muotoa, *īlot* ja *īlos*.** Wiki kuvaa vain datiivin <Hv>īlot</Hv>. Sarjassa ja wikin
esimerkeissä esiintyy kuitenkin myös <Hv l="īlon">īlos</Hv>, joka on *me*-pronominin vokatiivi. Kyse on
siis samasta vokatiivi + infinitiivi -rakenteesta, jolla käsky annetaan kolmannelle persoonalle:

<Example id="ex-bb8cf0f1a2de" />

<Line id="hotd-s3e03-006" />

Wiki ei kerro, eroavatko muodot merkitykseltään. Kirjaimellisesti datiivi on ’sallittakoon
meidän’, vokatiivi ’me tehkäämme’.
</Note>

## Toivotukset: vokatiivi + infinitiivi

Duolingosta tuttu kolmannen persoonan käsky on samalla valyrian **toivotus**. Kun subjekti ei ole
paikalla, käsky muuttuu toiveeksi:

<Example id="ex-322d35e66806" />

<Interlinear hv="Bōsirī zirys jemēb|agon." gloss="kauan hän-VOC hallita-INF" fi="Hallitkoon hän kauan." />

<Example id="ex-5fab68e6d60d" />

<Interlinear hv="Sȳrī avy ūjus dohaer|agon." gloss="hyvin sinä-ACC se-VOC palvella-INF" fi="Palvelkoon se sinua hyvin." />

<Compare glossed fi="hallit|koon" hv="zirys jemēb|agon" label="3. persoonan imperatiivi ~ vokatiivi + infinitiivi" />

Tässä suomi on vahvoilla: suomen **3. persoonan imperatiivi** (*hallitkoon, olkoon, eläköön*)
ilmaisee juuri tämän merkityksen. Suomessa merkitys näkyy verbissä, valyriassa sekä subjektissa
(vokatiivi) että verbissä (infinitiivi).

Kaunein esimerkki on Daenerysin tuutulaulu Drogonille:

<Line id="got-s7e04-001" />

<Interlinear hv="Ōdrys sōv|egon, qūvys sōv|egon, vȳs sōv|egon." gloss="kipu-VOC mennä.pois-INF kyynel-VOC mennä.pois-INF maailma-VOC mennä.pois-INF" fi="Kipu, mene pois; kyyneleet, menkää pois; maailma, mene pois." />

Kaikki kolme subjektia ovat vokatiivissa: Daenerys puhuttelee kipua, kyyneliä ja maailmaa.

### Eloton tekijä

Wikin kielioppisivu muistuttaa, että toivotuksissakin pätee Duolingon *Advanced valence* -osion
sääntö: eloton tekijä vaatii välinepassiivin. Siksi ei sanota *<Hv>ñuragon</Hv>* vaan
<Hv l="añuragon">añuragon</Hv>:

<Example id="ex-404a45b964aa" />

<Example id="ex-35d9d49abd3c" />

### Passiivinen toivotus

Kun käsky koskee kohdetta (”tulkoon tehdyksi”), kohde on **nominatiivissa** ja infinitiivi
passiivissa. Wikin esimerkit: <Hv glossed>Melva nevekson</Hv> ’kannettakoon päärynät’, <Hv glossed>Riña
nevekson</Hv> ’kannettakoon lapsi’. Sarjassa Jace toistaa isoisänsä käskyn pelkällä
passiivin infinitiivillä:

<Line id="hotd-s1e08-009" />

<Note>
Wikin mukaan verbejä <Hv>sagon</Hv> ja <Hv>ilagon</Hv> vältetään tällaisissa toivotuksissa. Ne ovat
kieliopillisesti mahdollisia mutta kömpelöitä, ja asia sanotaan mieluummin toisin.
</Note>

## Kielteiset käskyt – kertaus ja tarkennus

Kielteinen käsky on aina infinitiivi + <Hv>daor</Hv>, koska imperatiivia ei voi kieltää.

<Line id="hotd-s1e06-010" />

<Line id="hotd-s3e08-004" />

Wiki huomauttaa, että kielteisen vokatiivirakenteen voi tulkita kahdella tavalla: <Hv glossed>Dohaeriros māzigon
daor</Hv> voi tarkoittaa ’älköön orja tulko’ tai ’orja, älä tule’. Asiayhteys ratkaisee.

## Yhteenveto kirjeen kirjoittajalle

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Haluat sanoa</th><th>Rakenne</th><th>Esimerkki</th></tr></thead>
  <tbody>
    <tr><td>tee!</td><td>imperatiivi</td><td><Hv>bardūs</Hv></td></tr>
    <tr><td>teidän tulee (vastedes)</td><td>futuurin imperatiivi</td><td><Hv>iderēbilātās</Hv></td></tr>
    <tr><td>älä tee</td><td>infinitiivi + <Hv>daor</Hv></td><td><Hv fi="älä kirjoita">bardugon daor</Hv></td></tr>
    <tr><td>anna minun</td><td>datiivi + infinitiivi</td><td><Hv>ynot rēbagon</Hv></td></tr>
    <tr><td>tehdään</td><td><Hv>īlot</Hv> + infinitiivi</td><td><Hv fi="mennään">īlot jagon</Hv></td></tr>
    <tr><td>tehköön hän (toive)</td><td>vokatiivi + infinitiivi</td><td><Hv fi="hallitkoon hän">zirys jemēbagon</Hv></td></tr>
    <tr><td>tehtäköön</td><td>nominatiivi + passiivin infinitiivi</td><td><Hv fi="kannettakoon päärynät">melva nevekson</Hv></td></tr>
  </tbody>
</table>
</div>

## Harjoituslauseita

<Practice hv="Ynot bardugon." fi="Anna minun kirjoittaa." />

<Practice hv="Īlot ipradagon!" fi="Syödään!" />

<Practice hv="Sȳrī zirys ēdrugon." fi="Nukkukoon hän hyvin." />

<Practice hv="Hēzīr ynot bardīlās." fi="Tästä lähtien kirjoitat minulle." />

## Sanat

<Vocab />

<Check />
